import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner"; // Import loader
import { RoleGuard } from "../App";
import { StandardURL } from "../App";

const PendingPayments = () => {
  const [pendingData, setPendingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPendingData = async () => {
      setLoading(true);

      const token = localStorage.getItem("token");
      const userDataString = localStorage.getItem("user");
      const userData = JSON.parse(userDataString);
      const userId = userData.unique;
      const propertyId = localStorage.getItem("selectedPropertyId");

      try {
        const response = await axios.get(
      `${StandardURL}/iNyumba/admin/bill/viewabillandbalances/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setPendingData(response.data.response); // Adjust based on your response structure
        setLoading(false);
      } catch (error) {
        toast.error(error.message);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingData();
  }, []);

  const handleBack = () => {
    navigate(-1); // Go back to the previous page
  };

  // Render loading state while fetching data
  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200">
            <div className="flex justify-between items-center mb-6 sm:mb-8">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">
                Pending Payments 🗓️
              </h1>
              <button
                onClick={handleBack}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-300 ease-in-out"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Back
              </button>
            </div>
            <p className="text-gray-600 mb-8 max-w-2xl">
              This table displays all outstanding payments from tenants, providing a clear overview of who owes what. Prioritize follow-ups to ensure timely collections.
            </p>

            {/* Table Container */}
            <div className="overflow-x-auto relative shadow-lg rounded-lg border border-gray-100">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider rounded-tl-lg">Tenant Name</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Phone</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">House No.</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Water Bill</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Rent Bill</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Trash Bill</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Service Bill</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Billable Amount</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Amount Paid</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider rounded-tr-lg">Balance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {pendingData.length > 0 ? (
                    pendingData.map((pending, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-150 ease-in-out">
                        <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-gray-900">{pending.user_name}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-700">{pending.phone}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-700">{pending.house_no}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-blue-600 font-semibold">{pending.water_bill}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-green-600 font-semibold">{pending.rent_bill}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-purple-600 font-semibold">{pending.trashbill}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-blue-600 font-semibold">{pending.service_fee}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-800 font-bold">{pending.total_amount}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-emerald-700 font-semibold">{pending.amount_paid}</td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-red-600 font-bold">{pending.balance}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="py-8 px-4 text-center text-lg text-gray-500 italic">
                        <div className="flex flex-col items-center justify-center">
                            <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                            No pending payments found. All clear! 🎉
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default PendingPayments;
